import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { createBooking } from '@/api/generated/sdk.gen';
import { apartmentsQuery } from '@/lib/apartments';
import { bookingQuoteQuery, completePriceMinor, discountedMinor } from '@/lib/bookingQuote';
import { errorMessage } from '@/lib/errors';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-picker';
import { daysBetweenYMD, todayYMD } from '@/lib/calendar';
import { Stepper } from './Stepper';
import { GuestField, type GuestMode } from './GuestField';
import { PriceSection } from './PriceSection';
import { inputCls, labelCls } from './formStyles';
import type { Guest, CreateBookingRequest } from '@/api/generated/types.gen';

// ─── NewBookingForm ───────────────────────────────────────────────────────────

export interface NewBookingFormProps {
  onClose: () => void;
  /**
   * Calendar create-from-selection flow: seeds the apartment and the dates.
   * Seeds only — every field stays editable, because the operator both
   * mis-drags rows and needs stays longer than the visible window (OH-139).
   */
  prefill?: {
    apartmentId: string;
    checkin: string; // YYYY-MM-DD
    checkout: string; // YYYY-MM-DD
  };
}

/**
 * The part of a calendar selection that is still bookable. The grid renders
 * past months and its drag gesture has no past guard, so a selection can arrive
 * already expired — and a past stay is out (OH-139: with dynamic pricing there
 * is nothing to price). A range straddling today keeps its future half; a range
 * wholly in the past seeds nothing and the operator picks again.
 * YMD strings compare lexicographically, which is also chronologically.
 */
export function futurePrefillRange(
  prefill: NewBookingFormProps['prefill'],
  today: string,
): { checkin: string; checkout: string } {
  if (!prefill || prefill.checkout <= today) return { checkin: '', checkout: '' };
  return {
    checkin: prefill.checkin < today ? today : prefill.checkin,
    checkout: prefill.checkout,
  };
}

export function NewBookingForm({ onClose, prefill }: NewBookingFormProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: apartments } = useQuery(apartmentsQuery);

  const today = todayYMD();
  const [apartmentId, setApartmentId] = useState(prefill?.apartmentId ?? '');
  const [seed] = useState(() => futurePrefillRange(prefill, today));
  const [checkin, setCheckin] = useState(seed.checkin);
  const [checkout, setCheckout] = useState(seed.checkout);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [totalMajor, setTotalMajor] = useState('');
  const [allowOverbook, setAllowOverbook] = useState(false);

  // The stay is priced by the server on both entry points now. The final total
  // is `totalMajor` (what the submit body carries); the discount and a manual
  // override only decide what fills it.
  const [discountPct, setDiscountPct] = useState('');
  const nights = checkin && checkout ? daysBetweenYMD(checkin, checkout) : 0;
  const quoteQ = useQuery({
    ...bookingQuoteQuery(apartmentId, checkin, checkout, { adults, children, infants }),
    enabled: Boolean(apartmentId && checkin && checkout && nights >= 1),
  });
  const quote = quoteQ.data;

  // Autofill guard: remember exactly what we wrote into the field. We may
  // overwrite or clear it only while it still holds that value — anything the
  // operator typed survives every re-quote, discount edit and date change.
  // Before OH-139 a discount edit cleared the override flag, so the next
  // unavailable quote wiped a hand-typed total.
  const autoFilledRef = useRef<string | null>(null);
  const [manualTotal, setManualTotal] = useState(false);
  useEffect(() => {
    // A complete price is the bookable total, or the indicative one when every
    // night is priced. A partial sum never lands in the field (OH-139).
    const base = completePriceMinor(quote);
    const pct = parseFloat(discountPct);
    const next =
      base == null ? '' : (discountedMinor(base, Number.isFinite(pct) ? pct : 0) / 100).toFixed(2);
    setTotalMajor((cur) => {
      if (cur !== '' && cur !== autoFilledRef.current) return cur; // the operator's number
      autoFilledRef.current = next === '' ? null : next;
      return next;
    });
  }, [quote, discountPct]);

  function handleTotalChange(v: string) {
    setTotalMajor(v);
    setManualTotal(v !== '' && v !== autoFilledRef.current);
  }


  const [guestMode, setGuestMode] = useState<GuestMode>('search');
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [newGuestName, setNewGuestName] = useState('');
  const [newGuestEmail, setNewGuestEmail] = useState('');
  const [newGuestPhone, setNewGuestPhone] = useState('');

  const [formError, setFormError] = useState<string | null>(null);
  const [noAvailability, setNoAvailability] = useState(false);
  // Captured synchronously in handleSubmit before state is cleared; avoids
  // React 18 batching causing a stale read inside the async mutationFn.
  const sendOverbookRef = useRef(false);

  // Overbooking is only on the table while something actually blocks the stay:
  // nights the quote reports occupied, or a server refusal the quote missed.
  // The checkbox unmounts when that stops being true, but its state does not —
  // without this predicate a tick made on a blocked range would still ride
  // along after the operator moved the dates somewhere free.
  const overbookApplies = (quote?.blocked_nights?.length ?? 0) > 0 || noAvailability;

  const mut = useMutation({
    mutationFn: () => {
      const minor = Math.round(parseFloat(totalMajor) * 100);
      if (!apartmentId) throw new Error('Select an apartment.');
      if (!checkin) throw new Error('Check-in date is required.');
      if (!checkout) throw new Error('Check-out date is required.');
      // The picker cannot produce a past date and the prefill is clamped, so
      // this only fires if a future change reopens that door. The server would
      // take it: it refuses a past checkout, not a past checkin.
      if (checkin < today) throw new Error('Check-in cannot be in the past.');
      if (!Number.isFinite(minor) || minor < 0) throw new Error('Enter a valid total amount.');

      let guest: CreateBookingRequest['guest'];
      if (guestMode === 'search') {
        if (!selectedGuest) throw new Error('Select a guest or create a new one.');
        guest = { id: selectedGuest.id };
      } else {
        if (!newGuestName.trim()) throw new Error('Guest name is required.');
        guest = {
          name: newGuestName.trim(),
          email: newGuestEmail.trim() || undefined,
          phone: newGuestPhone.trim() || undefined,
        };
      }

      return createBooking({
        throwOnError: true,
        body: {
          apartment_id: apartmentId,
          checkin_date: checkin,
          checkout_date: checkout,
          adults,
          children: children > 0 ? children : undefined,
          infants: infants > 0 ? infants : undefined,
          total_amount_minor: minor,
          guest,
          allow_overbook: sendOverbookRef.current ? true : undefined,
        },
      });
    },
    onSuccess: (res) => {
      const id = res.data?.id;
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['ari', 'calendar'] });
      toast({ kind: 'success', title: 'Booking created' });
      onClose();
      if (id) {
        void navigate({ to: '/bookings/$id', params: { id } });
      } else {
        console.error('createBooking 201 missing id', res);
      }
    },
    onError: (err) => {
      const obj = typeof err === 'object' && err !== null ? (err as unknown as Record<string, unknown>) : {};
      const code = typeof obj['code'] === 'string' ? obj['code'] : '';
      const status = typeof obj['status'] === 'number' ? obj['status'] : 0;
      if (code === 'no_availability' || status === 409) {
        setNoAvailability(true);
        setFormError('No availability for these dates.');
        return;
      }
      setNoAvailability(false);
      setFormError(errorMessage(err));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Capture the overbook intent synchronously before clearing panel state,
    // so the async mutationFn sees the correct value regardless of batching.
    // The checkbox is now reachable before the server refuses (the quote
    // reports blocked nights), so the intent is the checkbox alone.
    sendOverbookRef.current = allowOverbook && overbookApplies;
    setFormError(null);
    setNoAvailability(false);
    mut.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Apartment — always editable: a calendar selection seeds it, and the
          operator who dragged the wrong row fixes it here (OH-139). */}
      <div>
        <label htmlFor="nbf-apt" className={labelCls}>
          Apartment *
        </label>
        <select
          id="nbf-apt"
          required
          value={apartmentId}
          onChange={(e) => setApartmentId(e.target.value)}
          className={inputCls}
        >
          <option value="">Select apartment…</option>
          {(apartments ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} — {a.title}
            </option>
          ))}
        </select>
      </div>

      {/* Dates — a real calendar, two months wide, no past. A month-long stay
          no longer depends on what fits the visible grid. */}
      <div>
        <p className={labelCls}>Dates *</p>
        <DateRangePicker
          value={{ from: checkin || undefined, to: checkout || undefined }}
          onChange={(r) => {
            setCheckin(r.from ?? '');
            setCheckout(r.to ?? '');
          }}
          placeholder="Pick dates"
          disabledBefore={todayYMD()}
          months={2}
        />
        {checkin && checkout && nights < 1 && (
          <p role="status" className="mt-1 text-xs text-destructive">
            Pick at least one night.
          </p>
        )}
        {nights >= 1 && (
          <p className="mt-1 text-xs text-muted">
            {nights} {nights === 1 ? 'night' : 'nights'}
          </p>
        )}
      </div>

      {/* Occupancy */}
      <div>
        <p className={labelCls + ' mb-2'}>Occupancy</p>
        <div className="flex flex-col gap-2">
          <Stepper label="Adults" value={adults} onChange={setAdults} min={1} />
          <Stepper label="Children" value={children} onChange={setChildren} />
          <Stepper label="Infants" value={infants} onChange={setInfants} />
        </div>
      </div>

      <PriceSection
        quote={quote}
        isLoading={quoteQ.isLoading}
        error={quoteQ.isError ? quoteQ.error : null}
        discountPct={discountPct}
        onDiscountChange={setDiscountPct}
        totalMajor={totalMajor}
        onTotalChange={handleTotalChange}
        manualTotal={manualTotal}
        overbookVisible={overbookApplies}
        allowOverbook={allowOverbook}
        onAllowOverbookChange={setAllowOverbook}
      />

      {/* Guest */}
      <div>
        <p className={labelCls + ' mb-2'}>Guest *</p>
        <GuestField
          mode={guestMode}
          onModeChange={(m) => {
            setGuestMode(m);
            setSelectedGuest(null);
            setNewGuestName('');
            setNewGuestEmail('');
            setNewGuestPhone('');
          }}
          selectedGuest={selectedGuest}
          onSelectGuest={setSelectedGuest}
          newName={newGuestName}
          newEmail={newGuestEmail}
          newPhone={newGuestPhone}
          onNewNameChange={setNewGuestName}
          onNewEmailChange={setNewGuestEmail}
          onNewPhoneChange={setNewGuestPhone}
        />
      </div>

      {/* No-availability error. The override checkbox itself lives in
          PriceSection, which shows it as soon as the quote reports blocked
          nights — before the server ever refuses. */}
      {noAvailability && (
        <p
          role="alert"
          className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          No availability for these dates.
        </p>
      )}

      {/* Other errors */}
      {formError && !noAvailability && (
        <p
          role="alert"
          className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          {formError}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button
          type="submit"
          variant="primary"
          loading={mut.isPending}
          disabled={mut.isPending || nights < 1}
        >
          {mut.isPending ? 'Creating…' : 'Create booking'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} disabled={mut.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
